// screens/marketplace/AdminOrderDetail.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Package,
  Truck,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  RefreshCw,
  X,
  Save,
  MapPin,
  User as UserIcon,
  CreditCard,
  History,
  Undo2,
  Check,
  Store,
  Users,
  Tag,
  ExternalLink,
  Hash,
  Wallet,
  Layers,
  Receipt,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import {
  PageShell,
  Field,
  Input,
  Select,
  Button,
  LoadingBlock,
  useTokens,
} from "./shared";
import { variantKeyLabel } from "./pricingApi";

/* ══════════════════════════════════════════════════════════
   Status ladder — source of truth (must match backend enum)
   ══════════════════════════════════════════════════════════ */

const STATUS_FLOW = [
  { value: "ORDER_CONFIRMED", label: "Order Confirmed" },
  { value: "SHOPPING_IN_PROGRESS", label: "Shopping in Progress" },
  { value: "PURCHASED", label: "Purchased" },
  {
    value: "IN_TRANSIT_TO_CHINA_WAREHOUSE",
    label: "In Transit to China Warehouse",
  },
  {
    value: "RECEIVED_AT_CHINA_WAREHOUSE",
    label: "Received at China Warehouse",
  },
  { value: "INSPECTION_IN_PROGRESS", label: "Inspection in Progress" },
  { value: "PREPARING_FOR_SHIPMENT", label: "Preparing for Shipment" },
  { value: "SHIPPED_FROM_CHINA", label: "Shipped from China" },
  { value: "ARRIVED_IN_GHANA", label: "Arrived in Ghana" },
  { value: "READY_FOR_PICKUP", label: "Ready for Pickup" },
  { value: "OUT_FOR_DELIVERY", label: "Out for Delivery" },
  { value: "DELIVERED_SUCCESSFULLY", label: "Delivered Successfully" },
];

const STATUS_INDEX = Object.fromEntries(
  STATUS_FLOW.map((s, i) => [s.value, i])
);
const FEE_TRIGGER_INDEX = STATUS_INDEX.ARRIVED_IN_GHANA;

const statusLabel = (value) => {
  if (value === "FAILED") return "Failed";
  if (value === "CANCELLED") return "Cancelled";
  return STATUS_FLOW.find((s) => s.value === value)?.label || value || "—";
};

const statusIndex = (value) => STATUS_INDEX[value] ?? -1;

/* ══════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════ */

const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

const fmtDate = (iso, opts) =>
  iso
    ? new Date(iso).toLocaleDateString(
        undefined,
        opts || { month: "short", day: "numeric", year: "numeric" }
      )
    : "—";

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const readApiError = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  const errs = data.errors;
  if (errs) {
    const parts = [];
    for (const [key, val] of Object.entries(errs)) {
      const arr = Array.isArray(val) ? val : [val];
      arr.forEach((msg) => {
        if (key === "non_field_errors" || key === "detail") parts.push(msg);
        else parts.push(`${key.replace(/_/g, " ")}: ${msg}`);
      });
    }
    if (parts.length) return parts.join(" ");
  }
  return fallback;
};

/* ══════════════════════════════════════════════════════════
   Tone helpers
   ══════════════════════════════════════════════════════════ */

const paymentTone = (t, status) => {
  const map = {
    PENDING: { fg: t.warning, bg: t.warningBg, label: "Awaiting payment" },
    COMPLETED: { fg: t.success, bg: t.successBg, label: "Paid" },
    FAILED: { fg: t.error, bg: t.errorBg, label: "Payment failed" },
    CANCELLED: { fg: t.muted, bg: t.subtle, label: "Cancelled" },
  };
  return map[status] || { fg: t.muted, bg: t.subtle, label: status || "—" };
};

const shippingPaymentTone = (t, status) => {
  const map = {
    PENDING: { fg: t.warning, bg: t.warningBg, label: "Shipping pending" },
    PARTIAL: { fg: t.accent, bg: t.hoverBg, label: "Partially paid" },
    COMPLETED: { fg: t.success, bg: t.successBg, label: "Shipping paid" },
    FAILED: { fg: t.error, bg: t.errorBg, label: "Shipping failed" },
  };
  return map[status] || { fg: t.muted, bg: t.subtle, label: status || "—" };
};

/* ══════════════════════════════════════════════════════════
   AdminOrderDetail
   ══════════════════════════════════════════════════════════ */

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const t = useTokens();

  const [order, setOrder] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(() => new Set());
  const [selectedItems, setSelectedItems] = useState(() => new Set());
  const [advanceModal, setAdvanceModal] = useState(null);
  const [feeModal, setFeeModal] = useState(null);
  const [bulkFeeModal, setBulkFeeModal] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const res = await authFetch(`/marketplace/orders/${id}/`);
        const data = await res.json();
        if (!res.ok) throw new Error(readApiError(data, "Could not load order"));
        setOrder(data.data);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        return;
      }

      try {
        const lr = await authFetch(`/marketplace/orders/${id}/logs/`);
        if (lr.ok) {
          const ld = await lr.json();
          setLogs(ld.data || []);
        } else {
          setLogs(undefined);
        }
      } catch {
        setLogs(undefined);
      }

      setLoading(false);
    },
    [authFetch, id]
  );

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => order?.items || [], [order]);
  const payment = order ? paymentTone(t, order.payment_status) : null;
  const paid = order?.payment_status === "COMPLETED";

  /* ── Actions ───────────────────────────────────────── */
  const toggleItem = (itemId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelect = (itemId) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((i) => i.id)));
    }
  };

  const openBulkAdvance = () => {
    if (selectedItems.size === 0) return;
    const selected = items.filter((i) => selectedItems.has(i.id));
    const minIdx = Math.min(...selected.map((i) => statusIndex(i.status)));
    setAdvanceModal({
      selectedItems: selected,
      minIdx,
      targetStatus: STATUS_FLOW[minIdx + 1]?.value || "",
    });
  };

  const submitBulkAdvance = async (payload) => {
    try {
      const res = await authFetch("/marketplace/orders/items/bulk-advance/", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Could not advance"));

      const updated = data.data?.length || 0;
      const failures = data.errors || [];
      if (failures.length > 0) {
        showToast(
          `${updated} updated · ${failures.length} skipped: ${failures[0].error}`,
          "warning"
        );
      } else {
        showToast(
          `${updated} item${updated === 1 ? "" : "s"} advanced`,
          "success"
        );
      }
      setAdvanceModal(null);
      setSelectedItems(new Set());
      await load({ silent: true });
    } catch (err) {
      showToast(err.message, "error");
      throw err;
    }
  };

  const submitSingleAdvance = async (item, targetStatus, notes) => {
    try {
      const body = { status: targetStatus };
      if (notes && notes.trim()) body.notes = notes.trim();
      const res = await authFetch(
        `/marketplace/orders/${id}/items/${item.id}/status/`,
        {
          method: "PATCH",
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Could not advance"));
      showToast("Item advanced", "success");
      await load({ silent: true });
    } catch (err) {
      showToast(err.message, "error");
      throw err;
    }
  };

  const submitFee = async (item, fee) => {
    try {
      const res = await authFetch(
        `/marketplace/orders/${id}/items/${item.id}/shipping-fee/`,
        {
          method: "PATCH",
          body: JSON.stringify({ shipping_fee: fee }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(readApiError(data, "Could not set fee"));
      showToast("Shipping fee updated", "success");
      setFeeModal(null);
      await load({ silent: true });
    } catch (err) {
      showToast(err.message, "error");
      throw err;
    }
  };

  const submitBulkFee = async (feeMap, onProgress) => {
    const entries = Object.entries(feeMap).filter(
      ([, v]) => v && Number(v) > 0
    );
    if (entries.length === 0) return;
    const errors = [];
    let done = 0;
    await Promise.all(
      entries.map(async ([itemId, fee]) => {
        try {
          const res = await authFetch(
            `/marketplace/orders/${id}/items/${itemId}/shipping-fee/`,
            {
              method: "PATCH",
              body: JSON.stringify({ shipping_fee: fee }),
            }
          );
          if (!res.ok) {
            const d = await res.json().catch(() => ({}));
            errors.push(readApiError(d, "Could not set fee"));
          }
        } catch (err) {
          errors.push(err.message);
        } finally {
          done += 1;
          onProgress?.(done, entries.length);
        }
      })
    );
    if (errors.length) {
      showToast(
        `${entries.length - errors.length} fees saved · ${
          errors.length
        } failed: ${errors[0]}`,
        "warning"
      );
    } else {
      showToast(
        `${entries.length} shipping fee${entries.length === 1 ? "" : "s"} set`,
        "success"
      );
    }
    setBulkFeeModal(false);
    await load({ silent: true });
  };

  const refund = async () => {
    if (!window.confirm("Refund this order? This cannot be undone.")) return;
    setRefunding(true);
    try {
      const res = await authFetch(`/marketplace/orders/${id}/refund/`, {
        method: "POST",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(readApiError(d, "Could not refund"));
      }
      showToast("Order refunded", "success");
      await load({ silent: true });
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setRefunding(false);
    }
  };

  /* ── Loading / error ──────────────────────────────── */
  if (loading) {
    return (
      <PageShell eyebrow="Orders" title="Order detail">
        <LoadingBlock label="Loading order…" />
      </PageShell>
    );
  }

  if (error || !order) {
    return (
      <PageShell eyebrow="Orders" title="Order detail">
        <div style={styles.errorBox(t)}>
          <AlertCircle size={24} color={t.error} />
          <div style={{ fontSize: 15, fontWeight: 700, color: t.heading }}>
            {error || "Order not found"}
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate("/marketplace/manage/orders")}
          >
            Back to orders
          </Button>
        </div>
      </PageShell>
    );
  }

  const shippingTone = shippingPaymentTone(t, order.shipping_payment_status);
  const canRefund = paid && !order.refunded_at;

  /* Items awaiting a fee — arrived, unpaid, no fee. */
  const itemsNeedingFee = items.filter(
    (it) =>
      statusIndex(it.status) >= FEE_TRIGGER_INDEX &&
      it.shipping_payment_status !== "COMPLETED" &&
      (!it.shipping_fee || Number(it.shipping_fee) === 0)
  );

  return (
    <PageShell
      eyebrow="Orders"
      title={`Order #${String(order.id).slice(0, 8)}`}
      subtitle={`Placed ${fmtDateTime(order.created_at)} · ${
        order.order_type_display || order.order_type || ""
      }`}
      actions={
        <>
          <Button
            variant="secondary"
            icon={ChevronLeft}
            onClick={() => navigate("/marketplace/manage/orders")}
          >
            Back
          </Button>
          <Button
            variant="secondary"
            icon={RefreshCw}
            onClick={() => load({ silent: true })}
          >
            Refresh
          </Button>
          {canRefund && (
            <Button
              variant="dangerGhost"
              icon={Undo2}
              onClick={refund}
              disabled={refunding}
            >
              {refunding ? "Refunding…" : "Refund"}
            </Button>
          )}
        </>
      }
    >
      {/* ── Payment gate ─────────────────────────────── */}
      {!paid && (
        <div style={styles.gateCard(t, payment.fg)}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: payment.bg,
                color: payment.fg,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Clock size={22} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: t.heading }}>
                {payment.label}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: t.muted,
                  marginTop: 3,
                  lineHeight: 1.55,
                }}
              >
                Fulfilment cannot begin until payment is confirmed. Items are
                visible below for reference, but cannot be advanced.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Hero summary ─────────────────────────────── */}
      <div style={styles.heroGrid}>
        <div style={styles.heroCard(t)}>
          <div style={styles.heroEyebrow(t)}>Payment</div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 8,
              flexWrap: "wrap",
            }}
          >
            <StatusPill
              fg={payment.fg}
              bg={payment.bg}
              label={payment.label}
              icon={
                order.payment_status === "COMPLETED"
                  ? CheckCircle2
                  : order.payment_status === "FAILED"
                  ? XCircle
                  : Clock
              }
            />
            {paid && (
              <span style={{ fontSize: 12.5, color: t.muted, fontWeight: 600 }}>
                {order.payment_status_display || order.payment_status}
              </span>
            )}
          </div>
          <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
            <Row
              label="Fulfilment"
              value={
                paid
                  ? order.status_display || statusLabel(order.status)
                  : "Locked until payment"
              }
              muted={!paid}
            />
            <Row
              label="Shipping"
              value={
                <span style={{ color: shippingTone.fg, fontWeight: 700 }}>
                  {shippingTone.label}
                </span>
              }
            />
            {order.shipping_fee_paid_at && (
              <Row
                label="Shipping paid"
                value={fmtDate(order.shipping_fee_paid_at)}
              />
            )}
            {order.refunded_at && (
              <Row
                label="Refunded"
                value={
                  <span style={{ color: t.error, fontWeight: 700 }}>
                    {fmtDate(order.refunded_at)}
                  </span>
                }
              />
            )}
          </div>
        </div>

        <div style={styles.heroCard(t)}>
          <div style={styles.heroEyebrow(t)}>Totals</div>
          <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
            <Row label="Subtotal" value={money(order.subtotal)} />
            <Row label="Product shipping" value={money(order.shipping_cost)} />
            <Row label="Fulfilment fee" value={money(order.shipping_fee)} />
            <Row label="Tax" value={money(order.tax)} />
          </div>
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: `1px solid ${t.separator || t.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <span style={{ fontSize: 12, color: t.muted, fontWeight: 700 }}>
              Grand total
            </span>
            <span
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: t.heading,
                letterSpacing: "-0.02em",
              }}
            >
              {money(order.grand_total)}
            </span>
          </div>
          {(Number(order.agent_profit) > 0 ||
            Number(order.sub_agent_profit) > 0) && (
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: `1px solid ${t.separator || t.border}`,
                display: "grid",
                gap: 4,
              }}
            >
              {Number(order.agent_profit) > 0 && (
                <Row
                  label="Agent profit"
                  value={
                    <span style={{ color: t.success, fontWeight: 700 }}>
                      {money(order.agent_profit)}
                    </span>
                  }
                />
              )}
              {Number(order.sub_agent_profit) > 0 && (
                <Row
                  label="Sub-agent profit"
                  value={
                    <span style={{ color: t.success, fontWeight: 700 }}>
                      {money(order.sub_agent_profit)}
                    </span>
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Fulfilment banner ────────────────────────── */}
      {paid && (
        <div style={styles.fulfilmentBanner(t)}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <Layers size={14} color={t.accent} />
            <span style={{ fontSize: 12, fontWeight: 800, color: t.heading }}>
              Fulfilment progress
            </span>
            <span style={{ fontSize: 11.5, color: t.muted }}>
              · roll-up of least-advanced item
            </span>
          </div>
          <OrderLadder order={order} t={t} />
        </div>
      )}

      {/* ── Contact + Address ─────────────────────────── */}
      <div style={styles.twoCol}>
        <div style={styles.card(t)}>
          <div style={styles.cardLabel(t)}>
            <UserIcon size={12} style={{ marginRight: 4 }} />
            Customer
          </div>
          <div style={{ marginTop: 8, display: "grid", gap: 3 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.heading }}>
              {[order.customer_first_name, order.customer_last_name]
                .filter(Boolean)
                .join(" ") ||
                order.customer_username ||
                order.customer?.username ||
                "—"}
            </div>
            {order.customer_username && (
              <div style={{ fontSize: 12, color: t.muted }}>
                @{order.customer_username}
              </div>
            )}
            {(order.customer_email || order.customer?.email) && (
              <div style={{ fontSize: 12.5, color: t.muted }}>
                {order.customer_email || order.customer.email}
              </div>
            )}
            {(order.customer_phone || order.customer?.phone_number) && (
              <div style={{ fontSize: 12.5, color: t.muted }}>
                {order.customer_phone || order.customer.phone_number}
              </div>
            )}
          </div>
        </div>

        <div style={styles.card(t)}>
          <div style={styles.cardLabel(t)}>
            <MapPin size={12} style={{ marginRight: 4 }} />
            Shipping address
          </div>
          {order.shipping_address ? (
            <div style={{ marginTop: 8, display: "grid", gap: 2 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: t.heading }}>
                {order.shipping_address.full_name}
              </div>
              <div style={{ fontSize: 12.5, color: t.text, lineHeight: 1.5 }}>
                {order.shipping_address.line1}
              </div>
              <div style={{ fontSize: 12.5, color: t.muted }}>
                {[order.shipping_address.city, order.shipping_address.region]
                  .filter(Boolean)
                  .join(", ")}
                {order.shipping_address.country &&
                  ` · ${order.shipping_address.country}`}
              </div>
              {order.shipping_address.phone && (
                <div style={{ fontSize: 12.5, color: t.muted, marginTop: 4 }}>
                  {order.shipping_address.phone}
                </div>
              )}
              {order.shipping_method && (
                <div style={{ fontSize: 11.5, color: t.muted, marginTop: 4 }}>
                  Method · {order.shipping_method}
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 8, fontSize: 12.5, color: t.muted }}>
              No address on file
            </div>
          )}
        </div>
      </div>

      {/* ── Agent / Sub-agent / Pre-order ────────────── */}
      <div style={styles.twoCol}>
        <div style={styles.card(t)}>
          <div style={styles.cardLabel(t)}>
            <Store size={12} style={{ marginRight: 4 }} />
            Agent
          </div>
          {order.agent || order.agent_username ? (
            <div style={{ marginTop: 8, display: "grid", gap: 2 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: t.heading }}>
                {order.agent_shop_name || order.agent_username || "—"}
              </div>
              {order.agent_username && (
                <div style={{ fontSize: 12, color: t.muted }}>
                  @{order.agent_username}
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 8, fontSize: 12.5, color: t.muted }}>
              No agent linked
            </div>
          )}
        </div>

        <div style={styles.card(t)}>
          <div style={styles.cardLabel(t)}>
            <Users size={12} style={{ marginRight: 4 }} />
            Sub-agent
          </div>
          {order.sub_agent || order.sub_agent_username ? (
            <div style={{ marginTop: 8, display: "grid", gap: 2 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: t.heading }}>
                {order.sub_agent_shop_name || order.sub_agent_username || "—"}
              </div>
              {order.sub_agent_username && (
                <div style={{ fontSize: 12, color: t.muted }}>
                  @{order.sub_agent_username}
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 8, fontSize: 12.5, color: t.muted }}>
              No sub-agent linked
            </div>
          )}
        </div>
      </div>

      {/* ── Pre-order + tracking ──────────────────────── */}
      {(order.pre_order_name ||
        order.tracking_number ||
        order.tracking_url) && (
        <div style={styles.twoCol}>
          {order.pre_order_name && (
            <div style={styles.card(t)}>
              <div style={styles.cardLabel(t)}>
                <Tag size={12} style={{ marginRight: 4 }} />
                Pre-order
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: t.heading,
                }}
              >
                {order.pre_order_name}
              </div>
            </div>
          )}
          {(order.tracking_number || order.tracking_url) && (
            <div style={styles.card(t)}>
              <div style={styles.cardLabel(t)}>
                <Truck size={12} style={{ marginRight: 4 }} />
                Tracking
              </div>
              <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
                {order.tracking_number && (
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: t.heading,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {order.tracking_number}
                  </div>
                )}
                {order.tracking_url && (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 700,
                      color: t.accent,
                      textDecoration: "none",
                    }}
                  >
                    Track shipment <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Transaction ──────────────────────────────── */}
      {order.transaction && (
        <div style={styles.card(t)}>
          <div style={styles.cardLabel(t)}>
            <CreditCard size={12} style={{ marginRight: 4 }} />
            Transaction
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginTop: 8,
            }}
          >
            <Row
              label="Reference"
              value={
                <span
                  style={{
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                  }}
                >
                  {order.transaction.reference}
                </span>
              }
            />
            <Row label="Amount" value={money(order.transaction.amount)} />
            <Row label="Status" value={order.transaction.status} />
            <Row
              label="Created"
              value={fmtDateTime(order.transaction.created_at)}
            />
          </div>
        </div>
      )}

      {/* ── Failure reason ───────────────────────────── */}
      {order.failure_reason && (
        <div style={styles.failureCard(t)}>
          <AlertCircle size={16} color={t.error} />
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: t.error }}>
              Failure reason
            </div>
            <div style={{ fontSize: 12.5, color: t.text, marginTop: 3 }}>
              {order.failure_reason}
            </div>
          </div>
        </div>
      )}

      {/* ── Items ─────────────────────────────────────── */}
      <section style={{ marginTop: 8 }}>
        <div style={styles.itemsHeader(t)}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 800,
                color: t.heading,
              }}
            >
              Items
            </h2>
            <span style={{ fontSize: 12.5, color: t.muted }}>
              {items.length} item{items.length === 1 ? "" : "s"}
            </span>
            {itemsNeedingFee.length > 0 && (
              <span style={styles.alertPill(t)}>
                <AlertCircle size={11} />
                {itemsNeedingFee.length} need fee
              </span>
            )}
          </div>
          {paid && items.length > 1 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {itemsNeedingFee.length > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={Wallet}
                  onClick={() => setBulkFeeModal(true)}
                >
                  Set fees
                </Button>
              )}
              <button
                type="button"
                onClick={selectAll}
                style={styles.textBtn(t)}
              >
                {selectedItems.size === items.length
                  ? "Clear selection"
                  : "Select all"}
              </button>
              {selectedItems.size > 0 && (
                <Button
                  size="sm"
                  icon={ChevronRight}
                  onClick={openBulkAdvance}
                >
                  Advance {selectedItems.size}
                </Button>
              )}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          {items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              orderId={id}
              paid={paid}
              expanded={expandedItems.has(item.id)}
              selected={selectedItems.has(item.id)}
              onToggleExpand={() => toggleItem(item.id)}
              onToggleSelect={() => toggleSelect(item.id)}
              onAdvance={submitSingleAdvance}
              onEditFee={() => setFeeModal({ item })}
              t={t}
            />
          ))}
        </div>
      </section>

      {/* ── Logs ───────────────────────────────────────── */}
      <section style={{ marginTop: 24 }}>
        <div style={styles.itemsHeader(t)}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <History size={14} color={t.accent} />
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 800,
                color: t.heading,
              }}
            >
              History
            </h2>
          </div>
        </div>
        <LogsTimeline logs={logs} items={items} t={t} />
      </section>

      {/* ── Notes / metadata ──────────────────────────── */}
      {(order.notes ||
        (order.metadata && Object.keys(order.metadata).length > 0)) && (
        <div style={{ ...styles.card(t), marginTop: 20 }}>
          {order.notes && (
            <>
              <div style={styles.cardLabel(t)}>Order notes</div>
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13.5,
                  color: t.text,
                  lineHeight: 1.6,
                }}
              >
                {order.notes}
              </div>
            </>
          )}
          {order.metadata && Object.keys(order.metadata).length > 0 && (
            <>
              <div
                style={{
                  ...styles.cardLabel(t),
                  marginTop: order.notes ? 14 : 0,
                }}
              >
                Metadata
              </div>
              <pre
                style={{
                  margin: "6px 0 0",
                  padding: 12,
                  borderRadius: 8,
                  background: t.subtle,
                  fontSize: 11.5,
                  color: t.text,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                  overflow: "auto",
                  maxHeight: 220,
                }}
              >
                {JSON.stringify(order.metadata, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────── */}
      {advanceModal && (
        <BulkAdvanceModal
          modal={advanceModal}
          t={t}
          onClose={() => setAdvanceModal(null)}
          onSubmit={submitBulkAdvance}
        />
      )}
      {feeModal && (
        <FeeModal
          item={feeModal.item}
          t={t}
          onClose={() => setFeeModal(null)}
          onSubmit={submitFee}
        />
      )}
      {bulkFeeModal && (
        <BulkFeeModal
          items={itemsNeedingFee}
          t={t}
          onClose={() => setBulkFeeModal(false)}
          onSubmit={submitBulkFee}
        />
      )}

      <style>{`
        @media (min-width: 720px) {
          .aod-two-col { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @keyframes aod-spin { to { transform: rotate(360deg); } }
      `}</style>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════
   Item row
   ══════════════════════════════════════════════════════════ */

const ItemRow = ({
  item,
  paid,
  expanded,
  selected,
  onToggleExpand,
  onToggleSelect,
  onAdvance,
  onEditFee,
  t,
}) => {
  const currentIdx = statusIndex(item.status);
  const nextStage = STATUS_FLOW[currentIdx + 1];
  const feeSet = Number(item.shipping_fee) > 0;
  const shippingPaid = item.shipping_payment_status === "COMPLETED";
  const shippingTone = shippingPaymentTone(t, item.shipping_payment_status);
  const arrived = currentIdx >= FEE_TRIGGER_INDEX;
  const canSetFee = arrived && !shippingPaid;
  const [advanceOpen, setAdvanceOpen] = useState(false);

  return (
    <div style={styles.itemCard(t, selected)}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {paid && (
          <button
            type="button"
            onClick={onToggleSelect}
            aria-label={selected ? "Deselect item" : "Select item"}
            style={styles.checkbox(t, selected)}
          >
            {selected && <Check size={12} color={t.accentText} />}
          </button>
        )}
        <div style={styles.thumb(t)}>
          {item.product_image ? (
            <img
              src={item.product_image}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Package size={18} color={t.muted} />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: t.heading,
                  letterSpacing: "-0.01em",
                }}
              >
                {item.product_name_display || item.product_name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: t.muted,
                  marginTop: 3,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span>Qty {item.quantity}</span>
                <span>·</span>
                <span>{money(item.price)} each</span>
                <span>·</span>
                <span style={{ color: t.text, fontWeight: 700 }}>
                  {money(item.total)}
                </span>
                {item.variant_key && (
                  <>
                    <span>·</span>
                    <span style={{ color: t.accent }}>
                      {variantKeyLabel(item.variant_key)}
                    </span>
                  </>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={onToggleExpand}
              aria-label={expanded ? "Collapse" : "Expand"}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                border: `1px solid ${t.border}`,
                background: "transparent",
                color: t.muted,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {expanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </button>
          </div>

          {/* Compact stepper */}
          <div style={{ marginTop: 10 }}>
            <CompactStepper
              currentIdx={currentIdx}
              currentLabel={item.status_display || statusLabel(item.status)}
              nextLabel={nextStage?.label}
              paid={paid}
              t={t}
            />
          </div>

          {/* Fee + shipping state row */}
          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {feeSet ? (
                <span style={styles.feeChip(t, shippingPaid)}>
                  <Truck size={11} />
                  {money(item.shipping_fee)}/unit ·{" "}
                  {money(item.shipping_total)}
                </span>
              ) : arrived ? (
                <span style={styles.feeChip(t, false, "muted")}>
                  <AlertCircle size={11} />
                  Fee not set
                </span>
              ) : (
                <span style={styles.feeChip(t, false, "muted")}>
                  <Truck size={11} />
                  Fee on arrival
                </span>
              )}
              <span
                style={{
                  ...styles.feeChip(t, shippingPaid),
                  background: shippingTone.bg,
                  color: shippingTone.fg,
                }}
              >
                {shippingTone.label}
              </span>
            </div>

            {paid && (
              <div style={{ display: "flex", gap: 6 }}>
                {canSetFee && (
                  <button
                    type="button"
                    onClick={onEditFee}
                    style={styles.smallActionBtn(t)}
                  >
                    {feeSet ? "Edit fee" : "Set fee"}
                  </button>
                )}
                {nextStage && (
                  <button
                    type="button"
                    onClick={() => setAdvanceOpen(true)}
                    style={{
                      ...styles.smallActionBtn(t),
                      background: t.accent,
                      color: t.accentText,
                      borderColor: t.accent,
                    }}
                  >
                    Advance
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Expanded detail */}
          {expanded && (
            <div
              style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: `1px solid ${t.separator || t.border}`,
                display: "grid",
                gap: 10,
              }}
            >
              <FullLadder currentIdx={currentIdx} t={t} />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 10,
                  marginTop: 4,
                }}
              >
                <Row label="Shipped at" value={fmtDateTime(item.shipped_at)} />
                <Row
                  label="Delivered at"
                  value={fmtDateTime(item.delivered_at)}
                />
                <Row
                  label="Fee paid at"
                  value={fmtDateTime(item.shipping_paid_at)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {advanceOpen && (
        <SingleAdvanceModal
          item={item}
          currentIdx={currentIdx}
          t={t}
          onClose={() => setAdvanceOpen(false)}
          onAdvance={async (status, notes) => {
            await onAdvance(item, status, notes);
            setAdvanceOpen(false);
          }}
        />
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Single-advance modal (with notes)
   ══════════════════════════════════════════════════════════ */

const SingleAdvanceModal = ({ item, currentIdx, t, onClose, onAdvance }) => {
  const [target, setTarget] = useState(
    STATUS_FLOW[currentIdx + 1]?.value || ""
  );
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!target) {
      setError("Pick a target status");
      return;
    }
    if (statusIndex(target) <= currentIdx) {
      setError("Target must be further along the ladder");
      return;
    }
    setSubmitting(true);
    try {
      await onAdvance(target, notes);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
      style={styles.modalOverlay(t)}
    >
      <form
        onSubmit={submit}
        style={{ ...styles.modalCard(t), maxWidth: 460 }}
      >
        <header style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle(t)}>Advance item</div>
            <div style={styles.modalSubtitle(t)}>
              {item.product_name_display || item.product_name} ×{item.quantity}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={styles.modalCloseBtn(t)}
          >
            <X size={16} />
          </button>
        </header>

        <Field label="Target status" error={error}>
          <Select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={submitting}
          >
            <option value="">Choose a stage…</option>
            {STATUS_FLOW.map((s, i) => (
              <option key={s.value} value={s.value} disabled={i <= currentIdx}>
                {s.label}
                {i <= currentIdx ? "  (locked)" : ""}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Notes (optional)">
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Arrived at Tema port"
            disabled={submitting}
          />
        </Field>

        <footer style={styles.modalFooter}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" icon={Save} disabled={submitting}>
            {submitting ? "Advancing…" : "Advance"}
          </Button>
        </footer>
      </form>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Steppers
   ══════════════════════════════════════════════════════════ */

const CompactStepper = ({ currentIdx, currentLabel, nextLabel, paid, t }) => {
  const total = STATUS_FLOW.length;
  const pct = paid && currentIdx >= 0 ? ((currentIdx + 1) / total) * 100 : 0;
  const isComplete = currentIdx >= total - 1;
  const isFailed = currentIdx === -1;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: t.muted,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            color: isFailed
              ? t.error
              : isComplete
              ? t.success
              : t.text,
          }}
        >
          {currentLabel}
        </span>
        {paid && !isFailed && !isComplete && (
          <span style={{ color: t.muted }}>
            · {currentIdx + 1}/{total}
          </span>
        )}
      </div>
      <div
        style={{
          marginTop: 6,
          height: 4,
          borderRadius: 2,
          background: t.hoverBg,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: isFailed
              ? t.error
              : isComplete
              ? t.success
              : t.accent,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      {paid && !isFailed && nextLabel && (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: t.muted,
            fontWeight: 500,
          }}
        >
          Next: {nextLabel}
        </div>
      )}
    </div>
  );
};

const FullLadder = ({ currentIdx, t }) => {
  return (
    <ol
      style={{
        margin: 0,
        padding: 0,
        listStyle: "none",
        display: "grid",
        gap: 2,
      }}
    >
      {STATUS_FLOW.map((s, i) => {
        const passed = i < currentIdx;
        const current = i === currentIdx;
        return (
          <li
            key={s.value}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 0",
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 7,
                border: `1.5px solid ${
                  current ? t.accent : passed ? t.accent : t.border
                }`,
                background: passed || current ? t.accent : "transparent",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {passed && <Check size={9} color={t.accentText} />}
              {current && (
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 3,
                    background: t.accentText,
                  }}
                />
              )}
            </span>
            <span
              style={{
                fontSize: 12.5,
                fontWeight: current ? 800 : passed ? 600 : 500,
                color: current ? t.heading : passed ? t.text : t.muted,
              }}
            >
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const OrderLadder = ({ order, t }) => {
  const currentIdx = statusIndex(order.status);
  const isTerminal =
    order.status === "FAILED" || order.status === "CANCELLED";
  const pct = isTerminal
    ? 0
    : currentIdx >= 0
    ? ((currentIdx + 1) / STATUS_FLOW.length) * 100
    : 0;
  const next = STATUS_FLOW[currentIdx + 1];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: t.muted,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.2,
            color: isTerminal ? t.error : t.text,
          }}
        >
          {order.status_display || statusLabel(order.status)}
        </span>
        {!isTerminal && currentIdx >= 0 && (
          <span style={{ color: t.muted }}>
            · step {currentIdx + 1} of {STATUS_FLOW.length}
          </span>
        )}
      </div>
      <div
        style={{
          height: 4,
          borderRadius: 2,
          background: t.hoverBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: isTerminal ? t.error : t.accent,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      {!isTerminal && next && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: t.muted }}>
          Next: {next.label}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Logs timeline (now resolves item product name)
   ══════════════════════════════════════════════════════════ */

const LogsTimeline = ({ logs, items, t }) => {
  const nameFor = (itemId) => {
    const it = items.find((i) => i.id === itemId);
    if (!it) return "Item";
    return it.product_name_display || it.product_name || "Item";
  };

  if (logs === undefined) {
    return (
      <div style={styles.logsEmpty(t)}>
        <History size={20} color={t.muted} />
        <p style={{ margin: "8px 0 0", fontSize: 13, color: t.muted }}>
          History is not available yet.
        </p>
      </div>
    );
  }

  if (logs === null) {
    return (
      <div style={styles.logsEmpty(t)}>
        <span
          style={{
            width: 16,
            height: 16,
            border: `2px solid ${t.border}`,
            borderTopColor: t.accent,
            borderRadius: "50%",
            animation: "aod-spin 0.8s linear infinite",
          }}
        />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div style={styles.logsEmpty(t)}>
        <p style={{ margin: 0, fontSize: 13, color: t.muted }}>
          No history entries yet.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${t.border}`,
        background: t.surfaceRaised,
        display: "grid",
        gap: 10,
      }}
    >
      {logs.map((log, i) => {
        const isLast = i === logs.length - 1;
        return (
          <div
            key={log.id || i}
            style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flexShrink: 0,
                paddingTop: 3,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === 0 ? t.accent : t.border,
                }}
              />
              {!isLast && (
                <span
                  style={{
                    width: 1,
                    flex: 1,
                    minHeight: 24,
                    background: t.border,
                    marginTop: 4,
                  }}
                />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 4 }}>
              <div
                style={{ fontSize: 13, fontWeight: 700, color: t.heading }}
              >
                {log.status_display || statusLabel(log.status)}
                {log.order_item ? (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      color: t.accent,
                    }}
                  >
                    · {nameFor(log.order_item)}
                  </span>
                ) : (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      color: t.muted,
                    }}
                  >
                    · Order-wide
                  </span>
                )}
              </div>
              {log.notes && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: t.text,
                    marginTop: 3,
                    lineHeight: 1.5,
                  }}
                >
                  {log.notes}
                </div>
              )}
              <div style={{ fontSize: 11.5, color: t.muted, marginTop: 3 }}>
                {fmtDateTime(log.created_at)}
                {log.changed_by ? ` · ${log.changed_by}` : ""}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Bulk advance modal — NO FEE INPUTS
   ══════════════════════════════════════════════════════════ */

const BulkAdvanceModal = ({ modal, t, onClose, onSubmit }) => {
  const { selectedItems, minIdx } = modal;
  const [targetStatus, setTargetStatus] = useState(modal.targetStatus || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  const targetIdx = statusIndex(targetStatus);
  const furthestLabel = STATUS_FLOW[minIdx]?.label || "—";

  const submit = async () => {
    setErrors({});
    setRowErrors({});
    if (!targetStatus) {
      setErrors({ status: "Pick a target status" });
      return;
    }
    if (targetIdx <= minIdx) {
      setErrors({ status: "Target must be further than the current stage" });
      return;
    }

    const items = selectedItems.map((it) => {
      const entry = { order_item_id: it.id, status: targetStatus };
      if (notes.trim()) entry.notes = notes.trim();
      return entry;
    });

    setSubmitting(true);
    try {
      await onSubmit({ items });
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
      style={styles.modalOverlay(t)}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={styles.modalCard(t)}
      >
        <header style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle(t)}>Advance selected items</div>
            <div style={styles.modalSubtitle(t)}>
              {selectedItems.length} item
              {selectedItems.length === 1 ? "" : "s"} · skipping stages allowed
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={styles.modalCloseBtn(t)}
          >
            <X size={16} />
          </button>
        </header>

        <div style={styles.infoStrip(t)}>
          <Hash size={12} />
          Current furthest-progressed item:{" "}
          <strong style={{ color: t.text, marginLeft: 4 }}>
            {furthestLabel}
          </strong>
        </div>

        <Field label="Target status" error={errors.status}>
          <Select
            value={targetStatus}
            onChange={(e) => setTargetStatus(e.target.value)}
            disabled={submitting}
          >
            <option value="">Choose a stage…</option>
            {STATUS_FLOW.map((s, i) => (
              <option key={s.value} value={s.value} disabled={i <= minIdx}>
                {s.label}
                {i <= minIdx ? "  (locked)" : ""}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Notes (optional, applied to all)">
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Arrived at Tema port"
            disabled={submitting}
          />
        </Field>

        <div style={{ display: "grid", gap: 6 }}>
          {selectedItems.map((it) => {
            const locked = it.shipping_payment_status === "COMPLETED";
            return (
              <div
                key={it.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: `1px solid ${locked ? t.warning + "55" : t.border}`,
                  background: locked ? t.warningBg + "55" : t.subtle,
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: t.border,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: t.muted,
                    flexShrink: 0,
                  }}
                >
                  <Package size={12} />
                </div>
                <div
                  style={{
                    flex: 1,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: t.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {it.product_name_display || it.product_name} ×{it.quantity}
                </div>
                {locked && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: "2px 7px",
                      borderRadius: 999,
                      background: t.warningBg,
                      color: t.warning,
                    }}
                  >
                    <AlertCircle size={10} />
                    Shipping paid
                  </span>
                )}
                {rowErrors[it.id] && (
                  <span
                    style={{
                      fontSize: 10.5,
                      color: t.error,
                      fontWeight: 600,
                    }}
                  >
                    {rowErrors[it.id]}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <footer style={styles.modalFooter}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" icon={Save} disabled={submitting}>
            {submitting ? "Advancing…" : "Advance selected"}
          </Button>
        </footer>
      </form>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Fee edit modal (single item)
   ══════════════════════════════════════════════════════════ */

const FeeModal = ({ item, t, onClose, onSubmit }) => {
  const [fee, setFee] = useState(
    Number(item.shipping_fee) > 0 ? String(item.shipping_fee) : ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const locked = item.shipping_payment_status === "COMPLETED";
  const arrived = statusIndex(item.status) >= FEE_TRIGGER_INDEX;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!arrived) {
      setError("Item hasn't arrived in Ghana yet.");
      return;
    }
    if (locked) {
      setError("Fee is locked after payment.");
      return;
    }
    if (fee === "" || Number(fee) < 0) {
      setError("Enter a valid fee");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(item, fee);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
      style={styles.modalOverlay(t)}
    >
      <form
        onSubmit={submit}
        style={{ ...styles.modalCard(t), maxWidth: 420 }}
      >
        <header style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle(t)}>Shipping fee</div>
            <div style={styles.modalSubtitle(t)}>
              {item.product_name_display || item.product_name} ×{item.quantity}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={styles.modalCloseBtn(t)}
          >
            <X size={16} />
          </button>
        </header>

        {locked ? (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              background: t.errorBg,
              color: t.error,
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            This fee is locked because the buyer has already paid shipping.
          </div>
        ) : !arrived ? (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              background: t.warningBg,
              color: t.warning,
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            Fee can only be set once the item has arrived in Ghana.
          </div>
        ) : (
          <>
            <Field label="Fee per unit (₵)" error={error}>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                disabled={submitting}
                placeholder="0.00"
                autoFocus
              />
            </Field>
            <div
              style={{
                marginTop: 6,
                fontSize: 11.5,
                color: t.muted,
                lineHeight: 1.5,
              }}
            >
              The buyer will pay{" "}
              <strong style={{ color: t.text }}>
                {money((Number(fee) || 0) * item.quantity)}
              </strong>{" "}
              for shipping on this item ({item.quantity} ×{" "}
              {money(Number(fee) || 0)}).
            </div>
          </>
        )}

        <footer style={styles.modalFooter}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {!locked && arrived && (
            <Button type="submit" icon={Save} disabled={submitting}>
              {submitting ? "Saving…" : "Save fee"}
            </Button>
          )}
        </footer>
      </form>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Bulk fee modal
   ══════════════════════════════════════════════════════════ */

const BulkFeeModal = ({ items, t, onClose, onSubmit }) => {
  const [feeMap, setFeeMap] = useState(() => {
    const m = {};
    items.forEach((it) => {
      m[it.id] = Number(it.shipping_fee) > 0 ? String(it.shipping_fee) : "";
    });
    return m;
  });
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: items.length });
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const missing = items.filter(
      (it) => !feeMap[it.id] || Number(feeMap[it.id]) < 0
    );
    if (missing.length) {
      setError("Enter a valid fee for every item.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(feeMap, (done, total) => setProgress({ done, total }));
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
      style={styles.modalOverlay(t)}
    >
      <form onSubmit={submit} style={styles.modalCard(t)}>
        <header style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle(t)}>Set shipping fees</div>
            <div style={styles.modalSubtitle(t)}>
              {items.length} item{items.length === 1 ? "" : "s"} awaiting a fee
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={styles.modalCloseBtn(t)}
          >
            <X size={16} />
          </button>
        </header>

        {error && (
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              padding: "8px 10px",
              borderRadius: 8,
              background: t.errorBg,
              color: t.error,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <AlertCircle size={12} />
            {error}
          </div>
        )}

        <div style={{ display: "grid", gap: 8 }}>
          {items.map((it) => (
            <div
              key={it.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 110px",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: t.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {it.product_name_display || it.product_name} ×{it.quantity}
              </div>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={feeMap[it.id] || ""}
                onChange={(e) =>
                  setFeeMap((m) => ({ ...m, [it.id]: e.target.value }))
                }
                disabled={submitting}
                placeholder="0.00"
              />
            </div>
          ))}
        </div>

        {submitting && (
          <div style={{ fontSize: 11.5, color: t.muted }}>
            Saving {progress.done}/{progress.total}…
          </div>
        )}

        <footer style={styles.modalFooter}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" icon={Save} disabled={submitting}>
            {submitting ? "Saving…" : "Save fees"}
          </Button>
        </footer>
      </form>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Small bits
   ══════════════════════════════════════════════════════════ */

const Row = ({ label, value, muted }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      fontSize: 12.5,
      color: muted ? "inherit" : undefined,
      opacity: muted ? 0.6 : 1,
    }}
  >
    <span style={{ color: "inherit", opacity: 0.75 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value}</span>
  </div>
);

const StatusPill = ({ fg, bg, label, icon: Icon }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      color: fg,
      background: bg,
    }}
  >
    {Icon && <Icon size={11} />}
    {label}
  </span>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  gateCard: (t, fg) => ({
    padding: 16,
    borderRadius: 14,
    border: `1px solid ${fg}55`,
    background: t.surfaceRaised,
    marginBottom: 16,
  }),
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 12,
    marginBottom: 16,
  },
  heroCard: (t) => ({
    padding: 16,
    borderRadius: 14,
    border: `1px solid ${t.border}`,
    background: t.surfaceRaised,
    minWidth: 0,
  }),
  heroEyebrow: (t) => ({
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: t.muted,
  }),
  twoCol: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 12,
    marginBottom: 16,
  },
  card: (t) => ({
    padding: 16,
    borderRadius: 14,
    border: `1px solid ${t.border}`,
    background: t.surfaceRaised,
    minWidth: 0,
  }),
  cardLabel: (t) => ({
    display: "flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: t.muted,
  }),
  fulfilmentBanner: (t) => ({
    padding: 16,
    borderRadius: 14,
    border: `1px solid ${t.border}`,
    background: t.surfaceRaised,
    marginBottom: 16,
  }),
  failureCard: (t) => ({
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    padding: 14,
    borderRadius: 12,
    border: `1px solid ${t.error}55`,
    background: t.errorBg,
    marginBottom: 16,
  }),
  infoStrip: (t) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 10px",
    borderRadius: 8,
    background: t.subtle,
    fontSize: 11.5,
    color: t.muted,
    fontWeight: 600,
  }),
  alertPill: (t) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 999,
    background: t.warningBg,
    color: t.warning,
    fontSize: 10.5,
    fontWeight: 800,
  }),
  itemsHeader: (t) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
    flexWrap: "wrap",
  }),
  itemCard: (t, selected) => ({
    padding: 14,
    borderRadius: 12,
    border: `1px solid ${selected ? t.accent : t.border}`,
    background: selected ? `${t.accent}0A` : t.surfaceRaised,
    transition: "border-color 0.15s ease, background 0.15s ease",
  }),
  checkbox: (t, selected) => ({
    width: 22,
    height: 22,
    borderRadius: 6,
    border: `1.5px solid ${selected ? t.accent : t.border}`,
    background: selected ? t.accent : "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    marginTop: 4,
  }),
  thumb: (t) => ({
    width: 48,
    height: 48,
    borderRadius: 9,
    overflow: "hidden",
    background: t.subtle,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  feeChip: (t, paid, muted) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 9px",
    borderRadius: 999,
    background: paid ? t.successBg : muted ? t.subtle : t.hoverBg,
    color: paid ? t.success : muted ? t.muted : t.text,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
  }),
  smallActionBtn: (t) => ({
    padding: "6px 12px",
    borderRadius: 8,
    border: `1px solid ${t.border}`,
    background: "transparent",
    color: t.text,
    fontSize: 11.5,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  }),
  textBtn: (t) => ({
    background: "transparent",
    border: "none",
    color: t.accent,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: 6,
    fontFamily: "inherit",
  }),
  logsEmpty: (t) => ({
    padding: 24,
    textAlign: "center",
    borderRadius: 12,
    border: `1px dashed ${t.border}`,
    background: t.surfaceRaised,
  }),
  errorBox: (t) => ({
    padding: 32,
    borderRadius: 16,
    border: `1px dashed ${t.border}`,
    background: t.surfaceRaised,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    textAlign: "center",
  }),
  modalOverlay: (t) => ({
    position: "fixed",
    inset: 0,
    background: t.scrim,
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  }),
  modalCard: (t) => ({
    background: t.surfaceRaised,
    border: `1px solid ${t.border}`,
    borderRadius: 16,
    width: "100%",
    maxWidth: 540,
    padding: 24,
    boxShadow: `0 24px 64px ${t.shadowStrong}`,
    display: "flex",
    flexDirection: "column",
    gap: 14,
    maxHeight: "90vh",
    overflowY: "auto",
  }),
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  modalTitle: (t) => ({
    fontSize: 16,
    fontWeight: 800,
    color: t.heading,
    letterSpacing: "-0.01em",
  }),
  modalSubtitle: (t) => ({
    fontSize: 12.5,
    color: t.muted,
    marginTop: 3,
  }),
  modalCloseBtn: (t) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "none",
    background: t.hoverBg,
    color: t.text,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    paddingTop: 8,
    marginTop: 4,
  },
};

/* Add responsive rules */
if (typeof document !== "undefined") {
  const styleTag = document.createElement("style");
  styleTag.innerHTML = `
    @media (min-width: 720px) {
      .aod-hero-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
      .aod-two-col  { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
    }
  `;
  // Only inject once
  if (!document.querySelector("style[data-aod-responsive]")) {
    styleTag.setAttribute("data-aod-responsive", "true");
    document.head.appendChild(styleTag);
  }
}